import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { Ref } from 'vue';

import { useSeoMeta, useServerSeoMeta } from '#imports';
import FooterController from '@/resources/storyblok/controllers/footer';
import HeaderController from '@/resources/storyblok/controllers/header';
import InspirationItemsController from '@/resources/storyblok/controllers/inspiration-items';
import EventBannersController from '@/resources/storyblok/controllers/event-banners';
import NotificationBarsController from '@/resources/storyblok/controllers/notification-bars';

import FlexLayoutController from '@/resources/storyblok/controllers/flex-layout';
import { useSeoStore } from '@/stores/seo';
import { storeToRefs } from 'pinia';


export const useStoryblokStore = defineStore('storyblok', () => {
  const globalComponents: Ref<Record<string, any>> = ref({});
  const pageData: Ref<any | null> = ref(null);
  const pageDataLoading: Ref<boolean> = ref(false);
  const seoStore = useSeoStore();
  const { seoImageUrl } = storeToRefs(seoStore);

  function parsePage(data: any) {
    const imagePath = data.content.rows?.[0]?.sections?.[0]?.columns?.[0]?.components?.[0]
    let imgData;
    switch (data.slug) {
      case 'malkurs-gutschein':
        imgData = imagePath?.headerImage?.filename
        break;
      case 'homepage':
        imgData = imagePath?.background?.desktopImage
        break;
      default:
        imgData = 'https://a.storyblok.com/f/112921/1462x796/eb27c034a1/an-logo-og.png'
        break;
    }

    if (imgData && seoImageUrl.value === null) {
      seoStore.getSeoImage(imgData)
      import.meta.server ? useServerSeoMeta({ ogImage: seoImageUrl.value }) : useSeoMeta({ ogImage: seoImageUrl.value })


    }

    return {
      ...data,
      content: FlexLayoutController(data.content),
    }
  }

  async function fetchPage(slug: string, version: string = 'published') {
    pageDataLoading.value = true;
    try {

      const data = await $fetch('/api/storyblok/content', { params: { slug, version } });

      if (data) {
        pageData.value = parsePage(data);
      } else {
        pageData.value = null;
      }
    } catch (error) {
      pageData.value = null;
      console.log('storyblok.ts', error)
    } finally {
      pageDataLoading.value = false;
    }
  }

  async function fetchGlobalComponents() {
    try {

      const { FooterItems, HeaderItems, NotificationbarsItems, EventbannersItems, InspirationsItems } = await $fetch('/api/storyblok/global-components')

      if (FooterItems) {
        const footer = FooterController(FooterItems.items[0]?.content);
        globalComponents.value.footer = footer;
      }

      if (HeaderItems) {
        const header = HeaderController(HeaderItems.items[0]?.content);
        globalComponents.value.header = header;
      }

      if (NotificationbarsItems) {
        const notificationBars = NotificationBarsController(NotificationbarsItems.items[0]?.content);
        globalComponents.value.NotificationbarsItems = notificationBars;
      }

      if (EventbannersItems) {
        const eventBanners = EventBannersController(EventbannersItems.items[0]?.content);
        globalComponents.value.EventbannersItems = eventBanners;
      }

      if (InspirationsItems) {
        const inspirations = InspirationItemsController(InspirationsItems.items[0]?.content);
        globalComponents.value.inspirationItems = inspirations;
      }
    } catch (error) {
      throw new Error('Failed to fetch global components.');
    }
  }

  const fetchPress = async () => {
    try {

      const { items } = await $fetch('/api/storyblok/press');
      globalComponents.value = {
        ...globalComponents.value,
        press: items[0]?.content,
      };
    } catch (err) {
      throw new Error('Failed to fetch press data.');
    }
  };

  return {
    pageData,
    pageDataLoading,
    globalComponents,
    parsePage,
    fetchPage,
    fetchPress,
    fetchGlobalComponents,
  };
});
